import {IPv4CIDR, IPv6CIDR} from '@/sphere/iputil';
import {apolloPlatformAxiosInstance as axiosInstance} from './apolloPlatformAxiosInstance';

export interface Domain {
  id: string;
  domain: string;
  created: Date;
  updated: Date;
}

export interface DomainVerification {
  id: string;
  domain: string;
  sentTo: string;
  expired: Date;
  validated: Date | undefined;
  created: Date;
  updated: Date;
}

export interface CreateVerification {
  domain: string;
  sentTo: string;
}

interface RawDomain {
  id: string;
  domain: string;
  created: string;
  updated: string;
}

interface RawDomainVerification {
  id: string;
  domain: string;
  sent_to: string;
  expired: string;
  validated: string;
  created: string;
  updated: string;
}

function rawToDv(dv: RawDomainVerification): DomainVerification {
  return {
    id: dv.id,
    sentTo: dv.sent_to,
    domain: dv.domain,
    expired: new Date(dv.expired),
    validated: dv.validated ? new Date(dv.validated) : undefined,
    created: new Date(dv.created),
    updated: new Date(dv.updated),
  };
}

export async function fetchDomains(): Promise<Domain[]> {
  const response = await axiosInstance.get('b2c/domains/');
  const rawDomains: RawDomain[] = response.data;
  return rawDomains.map((d: any) => ({
    id: d.id,
    domain: d.domain,
    created: new Date(d.created),
    updated: new Date(d.updated),
  }));
}

export async function deleteDomain(id: string): Promise<void> {
  await axiosInstance.delete(`b2c/domains/${id}/`, {
    validateStatus: (status) => status < 300 || status === 404,
  });
}

export async function fetchDomainVerifications(): Promise<DomainVerification[]> {
  const response = await axiosInstance.get('b2c/domain-verifications/');
  const rawItems: RawDomainVerification[] = response.data;
  return rawItems.map((dv: any) => rawToDv(dv));
}

export async function createDomainVerification(
  create: CreateVerification,
): Promise<DomainVerification> {
  const response = await axiosInstance.post('b2c/domain-verifications/', {
    domain: create.domain,
    sent_to: create.sentTo,
  });
  return rawToDv(response.data);
}

export async function deleteDomainVerification(id: string): Promise<void> {
  await axiosInstance.delete(`b2c/domain-verifications/${id}/`, {
    validateStatus: (status) => status < 300 || status === 404,
  });
}

export async function postClaimDomain(dvId: string, verificationCode: string): Promise<void> {
  await axiosInstance.post(`b2c/domain-verifications/${dvId}/claim/`, {
    verification_code: verificationCode,
  });
}

export interface LoginPolicy {
  id: string;
  version: number;
  basedOn?: string;
  basedOnVersion?: number;
  created: Date;
  updated: Date;
  frozen: boolean;
  active: boolean;
  contact: string;
  ipCheckEnabled: boolean;
  allowedIpRanges: string[];
  google: boolean;
  microsoft: boolean;
  email_otp: boolean;
}

interface RawLoginPolicy {
  id: string;
  version: number;
  based_on: string | null;
  based_on_version: number | null;
  created: string;
  updated: string;
  frozen: boolean;
  active: boolean;
  contact: string;
  ip_check_enabled: boolean;
  allowed_ip_ranges: string[];
  google: boolean;
  microsoft: boolean;
  email_otp: boolean;
}

function rawToLP(raw: RawLoginPolicy): LoginPolicy {
  return {
    id: raw.id,
    version: raw.version,
    basedOn: raw.based_on ?? undefined,
    basedOnVersion: raw.based_on_version ?? undefined,
    created: new Date(raw.created),
    updated: new Date(raw.updated),
    frozen: raw.frozen,
    active: raw.active,
    contact: raw.contact,
    ipCheckEnabled: raw.ip_check_enabled,
    allowedIpRanges: raw.allowed_ip_ranges,
    google: raw.google,
    microsoft: raw.microsoft,
    email_otp: raw.email_otp,
  };
}

export async function fetchLoginPolicies(): Promise<LoginPolicy[]> {
  const response = await axiosInstance.get('b2c/login-policies/');
  return response.data.map(rawToLP);
}

export type PatchLoginPolicy = {
  contact?: string;
  ipCheckEnabled?: boolean;
  allowedIpRanges?: (IPv4CIDR | IPv6CIDR | string)[];
  google?: boolean;
  microsoft?: boolean;
  email_otp?: boolean;
};

export async function patchLoginPolicy(id: string, patch: PatchLoginPolicy): Promise<LoginPolicy> {
  const patchBody: any = {};
  if (patch.contact !== undefined) patchBody.contact = patch.contact;
  if (patch.ipCheckEnabled !== undefined) patchBody.ip_check_enabled = patch.ipCheckEnabled;
  if (patch.allowedIpRanges !== undefined)
    patchBody.allowed_ip_ranges = patch.allowedIpRanges.map((range) => range.toString());
  if (patch.google !== undefined) patchBody.google = patch.google;
  if (patch.microsoft !== undefined) patchBody.microsoft = patch.microsoft;
  if (patch.email_otp !== undefined) patchBody.email_otp = patch.email_otp;

  const response = await axiosInstance.patch(`b2c/login-policies/${id}/`, patchBody);
  return rawToLP(response.data);
}

export async function postActivateLoginPolicy(
  toActivateId: string,
  oldPolicyId?: string,
): Promise<void> {
  await axiosInstance.post(`b2c/login-policies/${toActivateId}/activate/`, {
    old_policy: oldPolicyId,
  });
}

export async function postDeactivateLoginPolicy(toDeactivateId: string): Promise<void> {
  await axiosInstance.post(`b2c/login-policies/${toDeactivateId}/deactivate/`);
}

export async function deleteLoginPolicy(toDeleteId: string): Promise<void> {
  await axiosInstance.delete(`b2c/login-policies/${toDeleteId}/`);
}

export async function postCreateNewLoginPolicy(): Promise<LoginPolicy> {
  const response = await axiosInstance.post(`b2c/login-policies/`);
  return rawToLP(response.data);
}

export async function postCreateLoginPolicyFromExisting(existingId: string): Promise<LoginPolicy> {
  const response = await axiosInstance.post(`b2c/login-policies/${existingId}/create/`);
  return rawToLP(response.data);
}
